"use client";

import { usePollingTimeout } from "@/hooks/usePollTimer.hook";
import { getScannerTransactions } from "@/features/bridge-scanner/api/get-scanner-transactions";
import { useBridgeScannerStore } from "@/features/bridge-scanner/stores/bridge-scanner.store";
import { BSFilterParamEnum } from "@/types/bridge-scanner.types";
import type { NetworkType } from "@/types/general.types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "next/navigation";
import React, { useEffect } from "react";

const useBridgeScannerQuery = () => {
  const { liveMode, isControlWindowOpen } = useBridgeScannerStore();

  const { hasTimedOut } = usePollingTimeout(liveMode);

  const { network } = useParams() as { network: string };
  const searchParams = useSearchParams();

  const searchParamsObject = React.useMemo(() => {
    return {
      network: network,
      types: searchParams?.getAll("types"),
      fromChain: searchParams?.get(BSFilterParamEnum.SourceChain) || undefined,
      toChain: searchParams?.get(BSFilterParamEnum.TargetChain) || undefined,
      address: searchParams?.get("address") || undefined,
      fromDate: searchParams?.get(BSFilterParamEnum.FromDate) || undefined,
      toDate: searchParams?.get(BSFilterParamEnum.ToDate) || undefined,
    };
  }, [network, searchParams]);

  const transactionsQuery = useInfiniteQuery({
    queryKey: ["transactions", searchParamsObject, network],
    queryFn: async ({ pageParam }) => {
      const data = await getScannerTransactions({
        cursor: pageParam as string | undefined, // Use the cursor if there is one
        networkType: network as NetworkType,
        types: searchParamsObject.types?.length
          ? (searchParamsObject.types as string[]).join(",")
          : undefined,
        fromChains: searchParamsObject.fromChain as string,
        toChains: searchParamsObject.toChain as string,
        address: searchParamsObject.address as string,
        fromDate: searchParamsObject.fromDate as string,
        toDate: searchParamsObject.toDate as string,
        maxResults: 100, // Amount of results to retrieve from the db
      });
      return {
        items: data.bridgeData, // Make sure the API returns `items` and `nextCursor`
        nextCursor: data.nextCursor, // api must provide `nextCursor` for the next page
      };
    },
    initialPageParam: undefined as unknown,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined, // Get next page's cursor from response in cache
    refetchInterval: liveMode && !hasTimedOut ? 10000 : false,
    enabled: !isControlWindowOpen,
    staleTime: 1000 * 60,
    retryOnMount: false, // Don't retry on mount when coming back from transaction overview
    select: (data) => {
      if (!data) return null;
      return data?.pages.flatMap((page) => page.items); // Flatten the pages into a single list of items
    },
  });

  useEffect(() => {
    if (liveMode) transactionsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMode]);

  return transactionsQuery;
};

export default useBridgeScannerQuery;
